import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { toast } from 'sonner';
import { YField, YSelect, YBtn, YBadge } from '../../../../components/yool/YoolPrimitives';
import {
  getEmpresas,
  getPlanesHabilitados,
  setPolizaColectiva,
} from '../../../../services/empresasPlanesService';
import { getContratanteColectivoById } from '../../../../services/supabase/contratantesColectivosService';

export default function PasoEmpresaPlan({ polizaId, value, onChange, nombres }) {
  const [empresas, setEmpresas] = useState([]);
  const [habilitados, setHabilitados] = useState([]);
  const [loadingHabilitados, setLoadingHabilitados] = useState(false);
  const [linkingId, setLinkingId] = useState(null);
  const [buscandoContratante, setBuscandoContratante] = useState(false);
  // Este es el último paso — pedido real del usuario: en vez de repetir una
  // lista interactiva para un vínculo que ya quedó resuelto (auto o manual),
  // mostrar solo una confirmación. "Cambiar" reabre la lista completa por si
  // hay que corregir un vínculo real por error.
  const [mostrarTodos, setMostrarTodos] = useState(false);
  // `${polizaId}:${empresa_id}` ya evaluado para auto-vínculo — evita
  // reintentar en cada render, pero se re-evalúa si cambia la empresa
  // elegida dentro de este mismo paso.
  const autoVinculadoRef = useRef(null);

  // empresa_empleadores va por /api/empresas con el JWT de Clerk (ver el service).
  const { getToken } = useAuth();

  useEffect(() => {
    let ignore = false;
    getEmpresas(getToken)
      .then((data) => { if (!ignore) setEmpresas(data); })
      .catch((error) => console.warn('Error al obtener empresas:', error));
    return () => { ignore = true; };
  }, []);

  // empresa_planes_habilitados.plan_id es un plan totalmente distinto al
  // plan colectiva elegido en el paso 3 (ese es el plan MAESTRO de la
  // aseguradora; este es el plan específico habilitado para esta empresa,
  // casi siempre tipo_emision='individual') — no se filtran por plan exacto
  // entre sí. Pero SÍ deben compartir producto: una póliza colectiva es de
  // un producto (ej. "Protección de Bicicleta"), y ese producto puede tener
  // varios planes habilitados válidos para la empresa (ej. SMALL/MEDIUM/
  // LARGE) — mostrar planes de OTROS productos (dental, vida, etc.) sería
  // vincular una póliza con un plan que no corresponde a su línea de negocio.
  useEffect(() => {
    let ignore = false;
    if (!value?.empresa_id) { setHabilitados([]); return undefined; }
    setMostrarTodos(false);
    setLoadingHabilitados(true);
    getPlanesHabilitados(value.empresa_id)
      .then((data) => { if (!ignore) setHabilitados(data); })
      .catch((error) => console.warn('Error al obtener planes habilitados:', error))
      .finally(() => { if (!ignore) setLoadingHabilitados(false); });
    return () => { ignore = true; };
  }, [value?.empresa_id]);

  const recargarHabilitados = () => {
    if (!value?.empresa_id) return;
    getPlanesHabilitados(value.empresa_id)
      .then(setHabilitados)
      .catch((error) => console.warn('Error al obtener planes habilitados:', error));
  };

  // Contratante y empresa suelen ser la misma compañía real en la mayoría de
  // los casos (el contratante que firma con la aseguradora ES la empresa
  // cuyos colaboradores verán el plan) — pero son registros independientes
  // (contratantes_colectivos vs empresa_empleadores, sin FK entre ambos), así
  // que no se puede asumir el mismo id. Este botón busca por nombre en vez
  // de obligar a reelegir manualmente cuando en efecto coinciden.
  const handleUsarContratanteComoEmpresa = async () => {
    if (!value?.contratante_id) return;
    try {
      setBuscandoContratante(true);
      const contratante = await getContratanteColectivoById(value.contratante_id);
      const nombreContratante = (contratante?.nombre_razon_social || '').trim().toLowerCase();
      const match = empresas.find(
        (e) => (e.razon_social || '').trim().toLowerCase() === nombreContratante
      );
      if (!match) {
        toast.error(`No existe una empresa con el mismo nombre que el contratante ("${contratante?.nombre_razon_social || value.contratante_id}")`);
        return;
      }
      onChange({ empresa_id: match.id, empresa_plan_habilitado_id: '' });
      toast.success(`Empresa "${match.razon_social}" seleccionada`);
    } catch (e) {
      toast.error(e.message || 'Error al buscar el contratante');
    } finally {
      setBuscandoContratante(false);
    }
  };

  const habilitadosDelProducto = habilitados.filter(
    (h) => String(h.plan?.producto_id) === String(value?.producto_id)
  );

  // Una póliza respalda un solo SKU (plan habilitado) por producto: una vez
  // que YA hay uno vinculado a esta póliza, no tiene sentido seguir
  // listando los demás planes del mismo producto como opción — antes
  // quedaban visibles (aunque deshabilitados), y eso seguía dando la
  // impresión de que faltaba elegir algo (pedido real del usuario).
  const habilitadoVinculadoAqui = habilitadosDelProducto.find(
    (h) => h.poliza_colectiva_id && String(h.poliza_colectiva_id) === String(polizaId)
  );

  const handleVincular = async (habilitado, { automatico = false } = {}) => {
    if (habilitado.poliza_colectiva_id && String(habilitado.poliza_colectiva_id) !== String(polizaId)) {
      const ok = window.confirm(
        `"${habilitado.plan?.nombre || habilitado.plan_id}" ya está vinculado a otra póliza — ¿reemplazar?`
      );
      if (!ok) return;
    }

    try {
      setLinkingId(habilitado.id);
      await setPolizaColectiva(habilitado.id, polizaId);
      toast.success(
        automatico
          ? 'Único plan habilitado de este producto — vinculado automáticamente'
          : 'Póliza vinculada a la empresa'
      );
      onChange({ empresa_plan_habilitado_id: habilitado.id });
      recargarHabilitados();
      setMostrarTodos(false);
    } catch (e) {
      toast.error(e.message || 'Error al vincular la póliza');
    } finally {
      setLinkingId(null);
    }
  };

  // Auto-vincula cuando hay EXACTAMENTE un plan habilitado libre para el
  // producto de esta póliza: la póliza ya está asociada a un producto (paso
  // 2) y a un plan (paso 3) — si solo hay un plan habilitado posible para
  // esa empresa+producto, no hace falta pedirle a soporte que lo elija a
  // mano, basta con vincular la empresa. Si hay 0 (falta habilitarlo) o 2+
  // candidatos libres (ambiguo, o alguno ya está tomado por otra póliza),
  // no se puede decidir sola cuál usar — ahí sigue la selección manual.
  useEffect(() => {
    if (!polizaId || loadingHabilitados || !value?.producto_id || !value?.empresa_id) return;
    const key = `${polizaId}:${value.empresa_id}`;
    if (autoVinculadoRef.current === key) return;

    const yaVinculadaAqui = habilitadosDelProducto.some(
      (h) => h.poliza_colectiva_id && String(h.poliza_colectiva_id) === String(polizaId)
    );
    if (yaVinculadaAqui) {
      autoVinculadoRef.current = key;
      return;
    }

    const disponibles = habilitadosDelProducto.filter((h) => !h.poliza_colectiva_id);
    if (disponibles.length === 1) {
      autoVinculadoRef.current = key;
      handleVincular(disponibles[0], { automatico: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [habilitados, polizaId, loadingHabilitados, value?.producto_id, value?.empresa_id]);

  if (!polizaId) {
    return (
      <p className="y-field-hint">
        Guarda la póliza en el paso anterior antes de vincularla a una empresa.
      </p>
    );
  }

  return (
    <div className="y-form-body">
      <div className="y-form-section">
        <div className="y-form-section-head">
          <div className="y-form-section-eyebrow">Resumen de la póliza</div>
        </div>
        <div className="y-grid-2">
          <div>
            <div style={{ fontSize: 12, color: 'var(--yool-muted)' }}>Compañía</div>
            <div>{nombres?.compania || '—'}</div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: 'var(--yool-muted)' }}>Plan</div>
            <div>{nombres?.plan || '—'}</div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: 'var(--yool-muted)' }}>Número de póliza</div>
            <div>{value?.poliza?.numero_poliza || '—'}</div>
          </div>
        </div>
      </div>

      <div className="y-form-section">
        <YField label="Empresa" required>
          <YSelect
            data-testid="select-empresa"
            value={value?.empresa_id || ''}
            onChange={(e) => onChange({ empresa_id: e.target.value, empresa_plan_habilitado_id: '' })}
          >
            <option value="">Selecciona empresa</option>
            {empresas.map((e) => (
              <option key={e.id} value={e.id}>{e.razon_social}</option>
            ))}
          </YSelect>
        </YField>
        {value?.contratante_id && (
          <YBtn
            variant="outline"
            size="sm"
            onClick={handleUsarContratanteComoEmpresa}
            disabled={buscandoContratante}
          >
            {buscandoContratante ? 'Buscando...' : 'Usar el contratante como empresa'}
          </YBtn>
        )}
      </div>

      {value?.empresa_id && (
        <div className="y-form-section">
          <div className="y-form-section-head">
            <div className="y-form-section-eyebrow">Planes habilitados de esta empresa</div>
          </div>
          {loadingHabilitados ? (
            <p style={{ fontSize: 13, color: 'var(--yool-muted)' }}>Cargando...</p>
          ) : habilitados.length === 0 ? (
            <div className="y-list-empty">
              Esta empresa no tiene planes habilitados — habilítalos desde Empresas → Planes habilitados y vuelve a este paso.
            </div>
          ) : habilitadosDelProducto.length === 0 ? (
            <div className="y-list-empty">
              Esta empresa tiene planes habilitados, pero ninguno del producto de esta póliza — habilítalos desde Empresas → Planes habilitados y vuelve a este paso.
            </div>
          ) : habilitadoVinculadoAqui && !mostrarTodos ? (
            <div className="y-row" data-testid={`fila-habilitado-${habilitadoVinculadoAqui.id}`}>
              <div>
                <div className="y-row-head">
                  <YBadge tone="success" dot>Vinculada</YBadge>
                  <span className="y-row-title">
                    {habilitadoVinculadoAqui.nombre_interno || habilitadoVinculadoAqui.plan?.nombre || habilitadoVinculadoAqui.plan_id}
                  </span>
                </div>
              </div>
              <div className="y-row-right">
                <div className="y-row-actions">
                  <YBtn variant="quiet" size="sm" onClick={() => setMostrarTodos(true)}>
                    Cambiar
                  </YBtn>
                </div>
              </div>
            </div>
          ) : (
            <div>
              {habilitadosDelProducto.map((h) => {
                const vinculadaAqui = h.poliza_colectiva_id && String(h.poliza_colectiva_id) === String(polizaId);
                const vinculadaOtra = h.poliza_colectiva_id && !vinculadaAqui;
                return (
                  <div key={h.id} className="y-row" data-testid={`fila-habilitado-${h.id}`}>
                    <div>
                      <div className="y-row-head">
                        <span className="y-row-title">{h.nombre_interno || h.plan?.nombre || h.plan_id}</span>
                        <YBadge tone="outline">{h.ambiente}</YBadge>
                        {h.plan?.solo_empresa && <YBadge tone="cyan">Exclusivo</YBadge>}
                        {vinculadaAqui && <YBadge tone="success" dot>Vinculada</YBadge>}
                        {vinculadaOtra && <YBadge tone="warn" dot>Vinculada a otra póliza</YBadge>}
                      </div>
                    </div>
                    <div className="y-row-right">
                      <div className="y-row-actions">
                        <YBtn
                          size="sm"
                          variant={vinculadaAqui ? 'outline' : vinculadaOtra ? 'danger' : 'primary'}
                          onClick={() => handleVincular(h)}
                          disabled={linkingId === h.id || vinculadaAqui}
                        >
                          {linkingId === h.id
                            ? 'Vinculando...'
                            : vinculadaAqui
                              ? 'Vinculada'
                              : vinculadaOtra
                                ? 'Reemplazar vínculo'
                                : 'Vincular póliza'}
                        </YBtn>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
